import type { Era, Weighted, WeightedName } from '../types.js';

/**
 * French given names and surnames for synthetic BeurteletChatFun profiles.
 *
 * Given names follow births registered in France 1980–2008 (INSEE "Fichier
 * des prénoms"), weighted by approximate peak popularity on a 1–100 scale,
 * and include the Maghreb-heritage and West-African-heritage names that are
 * common in those cohorts. `eras` lists the cohorts a name was actually
 * given in, so a 1984-born profile is an Aurélie or a Julien, not a Jade.
 */

const A: Era = 'pre95';
const B: Era = '95-99';
const C: Era = '00-04';
const D: Era = '05-09';
const E: Era = 'post09';

function n(name: string, weight: number, ...eras: Era[]): WeightedName {
  return { name, weight, eras };
}

function s(name: string, weight: number): Weighted {
  return { name, weight };
}

export const FR_FEMALE: WeightedName[] = [
  // 1980s staples
  n('Aurélie', 70, A, B), n('Céline', 60, A), n('Stéphanie', 45, A), n('Sandrine', 30, A),
  n('Émilie', 65, A, B), n('Julie', 75, A, B, C), n('Audrey', 55, A, B), n('Élodie', 55, A, B),
  n('Laetitia', 45, A), n('Mélanie', 55, A, B), n('Virginie', 35, A), n('Sophie', 40, A),
  n('Caroline', 40, A), n('Karine', 25, A), n('Vanessa', 40, A), n('Jessica', 50, A, B),
  n('Amandine', 50, A, B), n('Anaïs', 55, A, B, C, D), n('Marion', 55, A, B), n('Laura', 70, A, B, C),
  n('Sarah', 75, A, B, C, D, E), n('Camille', 80, A, B, C, D, E), n('Pauline', 60, A, B, C), n('Marine', 60, A, B, C),
  n('Claire', 35, A), n('Charlotte', 50, A, B, C, D, E), n('Élise', 25, A, B), n('Hélène', 25, A),
  n('Lucie', 55, A, B, C, D, E), n('Mathilde', 45, A, B, C, D), n('Alexandra', 40, A, B), n('Cindy', 40, A),
  n('Coralie', 40, A, B), n('Delphine', 25, A), n('Valérie', 15, A), n('Isabelle', 15, A),
  n('Sabrina', 40, A, B), n('Sonia', 25, A, B), n('Nadia', 25, A), n('Samia', 20, A, B),
  n('Karima', 15, A), n('Amel', 20, A, B), n('Leïla', 25, A, B), n('Myriam', 25, A, B),
  n('Fatima', 20, A, B), n('Malika', 12, A), n('Nadège', 15, A), n('Christelle', 25, A),
  n('Cécile', 25, A), n('Magali', 20, A), n('Émeline', 25, A, B), n('Ophélie', 30, A, B),
  n('Gaëlle', 25, A, B), n('Aurore', 25, A, B), n('Angélique', 25, A), n('Séverine', 20, A),
  n('Laure', 15, A), n('Morgane', 45, A, B, C), n('Jennifer', 35, A), n('Elsa', 25, A, B, C, D),
  n('Alice', 35, A, B, C, D, E), n('Mélissa', 40, A, B, C), n('Estelle', 25, A, B), n('Fanny', 30, A, B),
  n('Justine', 50, A, B, C), n('Margaux', 45, A, B, C, D, E), n('Aline', 10, A), n('Johanna', 20, A, B),
  n('Carole', 12, A), n('Sandra', 25, A), n('Nawal', 15, A, B), n('Samira', 15, A),
  n('Soraya', 15, A, B), n('Kahina', 15, A, B, C), n('Djamila', 8, A), n('Nora', 20, A, B, C),
  n('Linda', 15, A), n('Lydia', 20, A, B), n('Wendy', 15, A, B), n('Priscilla', 12, A),
  n('Déborah', 20, A, B), n('Tiffany', 20, A, B), n('Cynthia', 20, A, B), n('Jordane', 8, A),
  n('Anne-Sophie', 15, A), n('Marie', 50, A, B, C, D), n('Anne', 15, A), n('Émilia', 8, A, B),
  n('Clémence', 30, A, B, C, D), n('Hanane', 12, A, B), n('Siham', 12, A, B), n('Ghislaine', 4, A),
  n('Fatiha', 10, A), n('Zohra', 8, A), n('Naïma', 12, A, B), n('Rachida', 8, A),
  n('Houria', 8, A), n('Yamina', 8, A), n('Nadjet', 6, A, B), n('Lamia', 10, A, B),
  n('Kelly', 25, A, B), n('Laurie', 20, A, B), n('Maud', 15, A), n('Perrine', 12, A),
  n('Aude', 10, A), n('Bérénice', 10, A, B), n('Gwendoline', 12, A, B), n('Mylène', 12, A),
  n('Rachel', 15, A, B), n('Roxane', 20, A, B), n('Tatiana', 10, A), n('Maureen', 10, A, B),
  n('Mélodie', 20, A, B), n('Nolwenn', 15, A, B), n('Marina', 20, A, B), n('Floriane', 12, A, B),
  n('Lorène', 8, A, B), n('Chrystelle', 6, A), n('Adeline', 15, A), n('Alexia', 20, A, B, C),
  n('Clarisse', 8, A, B), n('Diane', 12, A, B), n('Flavie', 8, A, B), n('Agnès', 6, A),
  n('Corinne', 6, A), n('Patricia', 5, A), n('Natacha', 10, A), n('Barbara', 10, A),
  n('Sylvia', 6, A), n('Jeanne', 15, A, D, E), n('Cassandra', 20, A, B, C), n('Kimberley', 12, B, C),

  // late-90s / 2000s cohort
  n('Manon', 90, B, C, D), n('Léa', 95, B, C, D, E), n('Chloé', 85, B, C, D, E), n('Emma', 80, B, C, D, E),
  n('Océane', 60, B, C), n('Clara', 55, B, C, D), n('Inès', 70, B, C, D, E), n('Margot', 30, B, C, D),
  n('Maëva', 30, B, C), n('Élisa', 30, B, C, D), n('Jade', 50, B, C, D, E), n('Lisa', 45, B, C, D),
  n('Romane', 45, B, C, D), n('Juliette', 40, B, C, D, E), n('Noémie', 40, B, C, D), n('Axelle', 30, B, C),
  n('Célia', 40, B, C, D), n('Laurine', 30, B, C), n('Alicia', 30, B, C), n('Amélie', 25, A, B),
  n('Mégane', 30, B, C), n('Lou', 30, B, C, D, E), n('Salomé', 35, B, C, D), n('Yasmine', 45, B, C, D),
  n('Imane', 35, B, C, D), n('Amira', 25, B, C, D), n('Kenza', 35, B, C, D), n('Nesrine', 20, B, C),
  n('Rania', 25, B, C, D), n('Khadija', 15, B, C), n('Assia', 25, B, C, D, E), n('Dounia', 15, B, C),
  n('Wassila', 6, B), n('Souad', 8, A, B), n('Maëlys', 45, C, D), n('Lola', 50, C, D, E),
  n('Eva', 50, C, D, E), n('Zoé', 45, C, D, E), n('Louise', 30, C, D, E), n('Lilou', 35, C, D),
  n('Ambre', 35, C, D, E), n('Léna', 45, C, D, E), n('Lina', 50, C, D, E), n('Nina', 35, C, D, E),
  n('Agathe', 20, C, D, E), n('Lucile', 15, B, C), n('Coline', 20, B, C, D), n('Héloïse', 15, C, D, E),
  n('Capucine', 15, C, D, E), n('Solène', 20, B, C), n('Maëlle', 30, C, D), n('Apolline', 15, C, D, E),
  n('Aya', 30, C, D, E), n('Nour', 30, C, D, E), n('Hajar', 15, C, D), n('Meryem', 15, C, D),
  n('Sofia', 30, C, D, E), n('Wiam', 10, C, D), n('Shaïna', 15, C, D), n('Malak', 15, C, D, E),
  n('Chaïma', 25, C, D), n('Hiba', 15, C, D), n('Kawtar', 10, C, D), n('Sirine', 15, C, D),
  n('Mélina', 20, C, D), n('Lindsay', 15, B, C), n('Laly', 10, D, E), n('Alyssa', 15, C, D),
  n('Elina', 15, C, D), n('Marie-Lou', 10, C, D), n('Louna', 25, C, D), n('Lana', 25, C, D, E),
  n('Rose', 20, D, E), n('Julia', 25, C, D, E), n('Mia', 20, D, E), n('Lise', 15, C, D),
  n('Éloïse', 15, C, D, E), n('Lily', 15, D, E), n('Anna', 20, D, E), n('Maya', 15, D, E),
  n('Lya', 15, D, E), n('Lyna', 25, C, D, E), n('Maryam', 20, D, E), n('Inaya', 15, D, E),
  n('Kayna', 10, D, E), n('Lilia', 15, C, D), n('Yousra', 12, C, D), n('Aïcha', 15, B, C, D),
  n('Anissa', 25, B, C, D), n('Sana', 15, C, D), n('Loubna', 12, B, C), n('Ikram', 12, C, D),
  n('Hind', 10, B, C), n('Hafsa', 10, C, D), n('Fatoumata', 20, B, C, D), n('Aminata', 15, B, C, D),
  n('Mariam', 15, C, D), n('Awa', 12, B, C, D), n('Kadiatou', 12, B, C), n('Binta', 8, C, D),
  n('Oumou', 8, C), n('Naomi', 15, C, D), n('Ilona', 15, C, D), n('Léane', 15, C, D),
  n('Léonie', 15, D, E), n('Louane', 20, C, D, E), n('Luna', 15, D, E), n('Maïlys', 15, C, D),
  n('Ornella', 10, B, C), n('Sixtine', 8, C, D), n('Valentine', 20, B, C, D), n('Victoria', 15, C, D),
  n('Violette', 10, D, E), n('Yaëlle', 8, C, D), n('Zélie', 8, D, E), n('Tessa', 10, C, D),
  n('Constance', 12, B, C), n('Daphné', 8, C), n('Éléonore', 10, C, D), n('Flora', 12, B, C),
  n('Joséphine', 10, D, E), n('Cloé', 15, C, D), n('Cassandre', 12, B, C), n('Marilou', 8, C, D),
  n('Maélie', 6, D), n('Lilwenn', 4, D, E), n('Albane', 8, C, D), n('Bertille', 4, C, D),
  n('Blanche', 6, D, E), n('Candice', 15, B, C), n('Charline', 15, B, C), n('Chanel', 6, C),
  n('Clélia', 8, C, D), n('Eden', 10, D, E), n('Enola', 10, D, E), 
  n('Fiona', 15, B, C), n('Gabrielle', 15, C, D, E), n('Garance', 8, C, D), n('Hanna', 15, C, D, E),
  n('Hannah', 12, C, D), n('Inaïa', 4, E), n('Iris', 12, D, E), 
  n('Jessy', 10, B), n('Jenna', 15, C, D), n('Kathleen', 6, B), n('Kiara', 8, D),
  n('Laïla', 10, C, D), n('Laurène', 15, B, C), n('Leelou', 6, D), n('Lena', 10, C, D),
  n('Lisa-Marie', 4, C), n('Lorie', 6, B, C), n('Louisa', 10, C, D), n('Ludivine', 15, A, B),
  n('Lucy', 6, D), n('Manel', 15, B, C, D), 
  n('Marwa', 15, B, C, D), n('Maryline', 6, A), n('Mélia', 6, D),
  n('Melissa', 10, B, C), n('Méline', 10, D), n('Mila', 15, D, E), n('Morgan', 8, A, B),
  n('Nadine', 4, A), n('Nahia', 4, D), n('Nayla', 12, D, E), n('Nelly', 8, A),
  n('Noa', 10, D, E), n('Norah', 10, D, E), n('Océanne', 4, C),
  n('Olivia', 12, D, E), n('Pénélope', 4, D), n('Philippine', 6, C), n('Rim', 6, C, D),
  n('Romy', 8, E), n('Ryma', 10, C, D), n('Safia', 12, A, B), n('Salma', 15, C, D, E),
  n('Selma', 10, C, D), n('Shana', 10, C), n('Sihem', 10, A, B), n('Sophia', 15, C, D),
  n('Thaïs', 8, D, E), n('Tiphaine', 10, A, B), n('Typhaine', 6, B),
  n('Wafa', 8, B), n('Warda', 6, A), n('Yasmina', 12, A, B), 
  n('Zahra', 12, C, D), n('Zineb', 12, B, C), n('Zoë', 4, D), n('Zaynab', 8, D, E),
  n('Ilham', 10, B, C), n('Ayla', 8, D, E), n('Sakina', 8, C, D),
  n('Asma', 12, B, C), n('Amina', 15, B, C, D), n('Farah', 15, B, C, D), n('Hayet', 6, A),
  n('Kamélia', 10, C, D), n('Lilya', 10, D), n('Mounia', 10, A, B), n('Nassima', 8, A, B),
  n('Ouarda', 4, A), n('Radia', 6, A), n('Sabah', 6, A), n('Saïda', 6, A),
  n('Sarra', 10, C, D), n('Selena', 8, C, D), n('Wided', 4, B), n('Yamna', 4, A),
  n('Dalila', 10, A), n('Nabila', 10, A), n('Ines', 10, C, D), n('Esma', 6, C, D),
  n('Alisson', 8, B, C), n('Anaëlle', 10, C, D), n('Annabelle', 8, A), 
  n('Carla', 15, C, D), n('Célina', 8, C, D), n('Chiara', 8, D, E), n('Elena', 12, C, D),
  n('Émy', 6, D), n('Erine', 6, D), n('Faustine', 6, B, C), n('Gwenaëlle', 8, A, B),
  n('Jordana', 2, B), n('Julianne', 4, C), n('Karen', 8, A),
  n('Katia', 10, A, B), n('Laëtitia', 20, A), n('Leslie', 10, A, B),
  n('Lou-Anne', 6, C, D), n('Mailys', 6, C, D), n('Manuela', 6, A),
  n('Marlène', 10, A), n('Méghane', 4, B), n('Mélany', 4, B), n('Noëlla', 4, B),
  n('Prisca', 4, A), n('Sabine', 4, A), 
  n('Sharon', 6, A, B), n('Steffi', 2, B), n('Vicky', 4, B), 
  n('Ana', 10, C, D), n('Inês', 4, C), n('Sara', 15, C, D), n('Daniela', 6, B),
];

export const FR_MALE: WeightedName[] = [
  // 1980s staples
  n('Kevin', 75, A, B), n('Julien', 75, A, B), n('Nicolas', 70, A, B), n('Sébastien', 45, A),
  n('Guillaume', 60, A, B), n('Romain', 65, A, B, C), n('Anthony', 60, A, B), n('Jérémy', 55, A, B),
  n('Mickaël', 40, A), n('Maxime', 70, A, B, C), n('Thomas', 85, A, B, C, D), n('Alexandre', 70, A, B, C),
  n('Benjamin', 50, A, B, C), n('Florian', 55, A, B), n('Jonathan', 45, A, B), n('Mathieu', 45, A, B),
  n('Cédric', 30, A), n('Damien', 35, A), n('Fabien', 30, A), n('Ludovic', 25, A),
  n('Olivier', 20, A), n('Christophe', 20, A), n('Vincent', 40, A, B), n('Laurent', 15, A),
  n('Stéphane', 15, A), n('Frédéric', 15, A), n('Grégory', 30, A), n('Yohan', 20, A, B),
  n('Jordan', 45, A, B), n('Dylan', 45, A, B, C), n('Loïc', 35, A, B), n('Arnaud', 30, A),
  n('Aurélien', 35, A, B), n('Bastien', 30, A, B), n('Clément', 55, A, B, C, D), n('Quentin', 60, A, B, C),
  n('Adrien', 40, A, B, C), n('Pierre', 45, A, B, C), n('Antoine', 55, A, B, C, D), n('Valentin', 45, A, B, C),
  n('David', 35, A), n('Jérôme', 25, A), n('Franck', 15, A), n('Cyril', 25, A),
  n('Mickael', 15, A), n('Michaël', 15, A), n('Steven', 20, A, B), n('Johnny', 10, A),
  n('Jimmy', 15, A), n('Teddy', 12, A, B), n('Rudy', 12, A), n('Yannick', 15, A),
  n('Sylvain', 25, A), n('Xavier', 15, A), n('Thierry', 5, A), n('Emmanuel', 15, A),
  n('Jean', 10, A), n('Jean-Baptiste', 20, A, B), n('François', 15, A), n('Matthieu', 25, A, B),
  n('Jérémie', 20, A, B), n('Joël', 5, A), n('Mehdi', 45, A, B, C), n('Karim', 30, A),
  n('Sofiane', 40, A, B, C), n('Samir', 25, A), n('Rachid', 20, A), n('Farid', 20, A),
  n('Mourad', 20, A), n('Hakim', 20, A), n('Djamel', 15, A), n('Nabil', 25, A, B),
  n('Kamel', 15, A), n('Hocine', 10, A), n('Salim', 20, A, B), n('Abdel', 10, A),
  n('Mustapha', 10, A), n('Khalid', 12, A), n('Redouane', 15, A, B), n('Nordine', 12, A),
  n('Kader', 10, A), n('Brahim', 12, A), n('Malik', 25, A, B, C), n('Hichem', 15, A, B),
  n('Fayçal', 15, A, B), n('Lyes', 15, A, B), n('Riad', 12, A, B), n('Sabri', 10, A, B),
  n('Jalil', 6, A), n('Abdelkader', 6, A), n('Mohamed', 45, A, B, C, D, E), n('Ahmed', 20, A, B),
  n('Mohammed', 25, A, B, C, D), n('Youssef', 30, A, B, C, D), n('Yacine', 30, A, B, C), n('Nassim', 30, B, C),
  n('Walid', 25, A, B, C), n('Adel', 15, A, B), n('Sami', 25, A, B, C), n('Moussa', 20, A, B, C),
  n('Mamadou', 25, A, B, C), n('Ibrahima', 15, B, C), n('Boubacar', 10, B, C), n('Abdoulaye', 12, B, C),
  n('Cheikh', 6, B, C), n('Idrissa', 8, B, C), n('Seydou', 6, B), n('Ousmane', 15, B, C),
  n('Gaël', 20, A, B), n('Erwan', 25, A, B, C), n('Ronan', 15, A, B),
  n('Tristan', 20, B, C, D), n('Simon', 30, A, B, C), n('Benoît', 20, A), n('Rémi', 25, A, B),
  n('Rémy', 20, A, B), n('Fabrice', 10, A), n('Pascal', 4, A), n('Patrick', 4, A),
  n('Gilles', 4, A), n('Marc', 10, A), n('Bruno', 8, A), n('Éric', 8, A),
  n('Thibault', 30, A, B, C), n('Thibaut', 20, A, B), n('Mathias', 20, B, C), n('Morgan', 25, A, B),
  n('Allan', 15, A, B), n('Alan', 12, A, B), n('Brandon', 20, A, B), n('Kenny', 10, A, B),
  n('Jason', 15, A, B), n('Mike', 5, A), n('Wilfried', 10, A), n('Grégoire', 15, A, B, C),
  n('Charles', 25, A, B, C, D), n('Martin', 25, A, B, C, D, E), n('Victor', 35, B, C, D, E), n('Paul', 50, A, B, C, D, E),

  // late-90s / 2000s cohort
  n('Lucas', 95, B, C, D, E), n('Hugo', 85, B, C, D, E), n('Théo', 85, B, C, D, E), n('Nathan', 90, B, C, D, E),
  n('Enzo', 70, B, C, D), n('Mathis', 60, B, C, D), n('Louis', 60, B, C, D, E), n('Tom', 50, B, C, D, E),
  n('Baptiste', 50, B, C, D), n('Corentin', 45, B, C), n('Alexis', 55, A, B, C, D), n('Raphaël', 45, C, D, E),
  n('Arthur', 50, C, D, E), n('Jules', 50, C, D, E), n('Gabriel', 50, C, D, E), n('Léo', 70, C, D, E),
  n('Noah', 40, D, E), n('Ethan', 45, C, D, E), n('Mattéo', 45, C, D, E), n('Timéo', 30, D, E),
  n('Sacha', 30, C, D, E), n('Evan', 35, C, D, E), n('Nolan', 35, C, D, E), n('Axel', 40, B, C, D),
  n('Killian', 35, B, C, D), n('Kylian', 15, C, D, E), n('Maël', 25, C, D, E), n('Gabin', 20, D, E),
  n('Liam', 20, D, E), n('Adam', 50, C, D, E), n('Rayan', 50, B, C, D, E), n('Yanis', 55, B, C, D),
  n('Bilal', 30, B, C, D), n('Amine', 40, B, C, D), n('Nassime', 6, C), n('Ilyes', 30, C, D),
  n('Anis', 30, B, C, D), n('Ryan', 40, B, C, D), n('Ismaël', 25, B, C, D, E), n('Ibrahim', 25, B, C, D, E),
  n('Ayoub', 30, B, C, D), n('Hamza', 30, B, C, D, E), n('Imran', 25, C, D, E), n('Younes', 30, B, C, D),
  n('Zakaria', 25, B, C, D), n('Soufiane', 25, B, C), n('Ilias', 20, C, D), n('Wassim', 25, B, C, D),
  n('Badr', 10, B, C), n('Othmane', 10, C, D), n('Idriss', 15, C, D, E), n('Marwan', 20, C, D),
  n('Haroun', 10, C, D), n('Aymen', 15, C, D), n('Issa', 10, C, D), n('Selim', 15, C, D),
  n('Yassine', 35, B, C, D), n('Ilan', 20, C, D, E), n('Kaïs', 15, C, D, E), n('Naïm', 15, C, D, E),
  n('Ayman', 15, C, D, E), n('Amir', 20, C, D, E), n('Omar', 20, B, C, D, E), n('Yasser', 10, B, C),
  n('Elias', 20, C, D, E), n('Ali', 20, B, C, D), n('Souleymane', 15, B, C, D), n('Mohamed-Amine', 15, C, D),
  n('Abdallah', 8, C, D), n('Rayane', 25, B, C, D), n('Reda', 15, B, C),
  n('Adil', 10, A, B), n('Lounis', 10, C, D), n('Massinissa', 6, B, C), 
  n('Aksel', 10, D, E), n('Malo', 15, D, E), n('Lenny', 20, C, D), n('Lorenzo', 20, C, D, E),
  n('Matteo', 15, D, E), n('Mattis', 10, D), n('Noé', 25, C, D, E), n('Robin', 25, B, C, D),
  n('Romaric', 4, A), n('Samuel', 30, B, C, D), n('Tanguy', 12, A, B, C),
  n('Titouan', 20, C, D), n('Ugo', 10, C, D), n('Valentino', 4, D), n('Yohann', 15, A, B),
  n('Esteban', 20, C, D), n('Diego', 10, C, D), n('Loris', 15, B, C, D),
  n('Mattia', 4, D), n('Kenzo', 15, C, D, E), n('Kelyan', 10, C, D), n('Kévin', 20, A, B),
  n('Jordy', 8, A, B), n('Dorian', 20, B, C), n('Damian', 4, C), n('Florent', 25, A, B),
  n('Jonas', 8, C, D), n('Hadrien', 10, B, C), n('Luca', 15, C, D, E),
  n('Luka', 10, D, E), n('Nils', 10, C, D), n('Oscar', 10, D, E), n('Owen', 10, D, E),
  n('Pablo', 10, C, D), n('Rafael', 8, D, E), n('Sohan', 6, D), n('Thiago', 8, D, E),
  n('Timothée', 15, B, C, D), n('Tony', 15, A, B), n('Warren', 6, B), n('William', 15, B, C),
  n('Yann', 25, A, B), n('Yoann', 20, A, B), n('Zinedine', 10, B), 
  n('Djibril', 15, B, C, D), n('Amadou', 15, B, C), n('Lamine', 12, B, C),
  n('Modibo', 4, B), n('Mamoudou', 4, B), n('Sekou', 8, B, C), n('Bakary', 8, B, C),
  n('Hamidou', 4, B), n('Oumar', 10, B, C), n('Alassane', 8, B, C), n('Saïd', 12, A),
  n('Yazid', 8, A, B), n('Nadir', 10, A, B), n('Mounir', 12, A), n('Tarek', 12, A, B),
  n('Fouad', 10, A), n('Zakarya', 6, C), n('Saad', 6, C, D), n('Ilyas', 15, C, D, E),
  n('Ismail', 10, B, C), n('Isaac', 12, C, D, E), n('Jibril', 8, C, D), n('Hamid', 8, A),
  n('Hassan', 10, A, B), n('Hicham', 12, A, B), n('Abdelaziz', 4, A), n('Aziz', 8, A, B),
  n('Bachir', 4, A), n('Chakib', 4, A), n('Dris', 4, A),
  n('Fares', 15, B, C), n('Faris', 6, C), n('Ghani', 4, B), n('Habib', 6, A),
  n('Jamel', 12, A), n('Kamal', 6, A), n('Khaled', 10, A, B), n('Lotfi', 8, A),
  n('Mahdi', 12, C, D), n('Messaoud', 4, A), n('Mokhtar', 4, A), n('Nacer', 6, A),
  n('Rafik', 10, A), n('Rami', 10, B, C), n('Ramzi', 8, A, B),
  n('Rédha', 4, B), n('Samy', 20, B, C), n('Tayeb', 4, A),
  n('Toufik', 8, A), n('Wahid', 4, A), n('Yahia', 6, A, B), n('Younès', 8, C, D),
  n('Zaki', 6, C), n('Zyad', 8, C, D), n('Ziad', 12, C, D), 
  n('Alexy', 6, C), 
  n('Antonin', 15, B, C, D), n('Augustin', 15, C, D, E), n('Basile', 10, D, E), n('Bryan', 25, A, B, C),
  n('Calvin', 8, C), n('Cyprien', 6, C, D), n('Dimitri', 15, A, B), n('Édouard', 10, C, D),
  n('Eliott', 15, D, E), n('Émile', 15, D, E), n('Étienne', 15, A, B, C),
  n('Félix', 15, C, D, E), n('Gauthier', 12, B, C), n('Geoffrey', 15, A, B), n('Gaëtan', 25, A, B),
  n('Henri', 8, D, E), n('Jean-Luc', 2, A), n('Joris', 10, A, B), n('Joshua', 12, B, C),
  n('Julian', 10, B, C), n('Justin', 15, B, C, D), n('Kilian', 15, B, C), n('Léon', 15, D, E),
  n('Logan', 25, B, C, D), n('Louka', 10, D), n('Lukas', 8, C, D), n('Mael', 8, C, D),
  n('Marius', 12, D, E), n('Marvin', 20, A, B, C), n('Max', 8, C, D), n('Maximilien', 8, B, C),
  n('Melvin', 15, B, C), n('Milan', 15, D, E), n('Nathanaël', 10, C, D),
  n('Noam', 15, D, E), 
  n('Raphael', 10, C, D), n('Ruben', 10, C, D), n('Sam', 8, C, D),
  n('Swann', 8, C, D), n('Teo', 6, D), n('Terence', 6, A),
  n('Tiago', 6, D), n('Tim', 6, D), 
  n('Vivien', 10, A, B), n('Wesley', 8, A, B), 
];

/** Common surnames in France, weighted by frequency. */
export const FR_SURNAMES: Weighted[] = [
  s('Martin', 100), s('Bernard', 60), s('Thomas', 55), s('Petit', 50), s('Robert', 50),
  s('Richard', 48), s('Durand', 46), s('Dubois', 45), s('Moreau', 44), s('Laurent', 42),
  s('Simon', 40), s('Michel', 40), s('Lefebvre', 38), s('Leroy', 37), s('Roux', 36),
  s('David', 35), s('Bertrand', 34), s('Morel', 33), s('Fournier', 32), s('Girard', 31),
  s('Bonnet', 30), s('Dupont', 30), s('Lambert', 29), s('Fontaine', 29), s('Rousseau', 28),
  s('Vincent', 28), s('Muller', 27), s('Lefèvre', 27), s('Faure', 26), s('André', 26),
  s('Mercier', 26), s('Blanc', 25), s('Guérin', 25), s('Boyer', 24), s('Garnier', 24),
  s('Chevalier', 24), s('François', 23), s('Legrand', 23), s('Gauthier', 23), s('Garcia', 23),
  s('Perrin', 22), s('Robin', 22), s('Clément', 22), s('Morin', 22), s('Nicolas', 21),
  s('Henry', 21), s('Roussel', 21), s('Mathieu', 20), s('Gautier', 20), s('Masson', 20),
  s('Marchand', 20), s('Duval', 19), s('Denis', 19), s('Dumont', 19), s('Marie', 19),
  s('Lemaire', 18), s('Noël', 18), s('Meyer', 18), s('Dufour', 18), s('Meunier', 18),
  s('Brun', 17), s('Blanchard', 17), s('Giraud', 17), s('Joly', 17), s('Rivière', 17),
  s('Lucas', 16), s('Brunet', 16), s('Gaillard', 16), s('Barbier', 16), s('Arnaud', 16),
  s('Martinez', 16), s('Gérard', 15), s('Roche', 15), s('Renard', 15), s('Schmitt', 15),
  s('Roy', 15), s('Leroux', 15), s('Colin', 14), s('Vidal', 14), s('Caron', 14),
  s('Picard', 14), s('Roger', 14), s('Fabre', 14), s('Aubert', 13), s('Lemoine', 13),
  s('Renaud', 13), s('Dumas', 13), s('Lacroix', 13), s('Olivier', 13), s('Philippe', 12),
  s('Bourgeois', 12), s('Pierre', 12), s('Benoît', 12), s('Rey', 12), s('Leclerc', 12),
  s('Payet', 12), s('Rolland', 11), s('Leclercq', 11), s('Guillaume', 11), s('Lecomte', 11),
  s('Lopez', 11), s('Jean', 11), s('Dupuy', 11), s('Guillot', 10), s('Hubert', 10),
  s('Berger', 10), s('Carpentier', 10), s('Sanchez', 10), s('Dupuis', 10), s('Moulin', 10),
  s('Louis', 10), s('Deschamps', 10), s('Huet', 9), s('Vasseur', 9), s('Perez', 9),
  s('Boucher', 9), s('Fleury', 9), s('Royer', 9), s('Klein', 9), s('Jacquet', 9),
  s('Adam', 9), s('Paris', 9), s('Poirier', 9), s('Marty', 8), s('Aubry', 8),
  s('Guyot', 8), s('Carré', 8), s('Charles', 8), s('Renault', 8), s('Charpentier', 8),
  s('Ménard', 8), s('Maillard', 8), s('Baron', 8), s('Bertin', 8), s('Bailly', 8),
  s('Hervé', 7), s('Schneider', 7), s('Fernandez', 7), s('Le Gall', 7), s('Collet', 7),
  s('Léger', 7), s('Bouvier', 7), s('Julien', 7), s('Prévost', 7), s('Millet', 7),
  s('Perrot', 7), s('Daniel', 7), s('Le Roux', 7), s('Cousin', 7), s('Germain', 7),
  s('Breton', 6), s('Besson', 6), s('Langlois', 6), s('Rémy', 6), s('Le Goff', 6),
  s('Pelletier', 6), s('Lévêque', 6), s('Perrier', 6), s('Leblanc', 6), s('Barré', 6),
  s('Lebrun', 6), s('Marchal', 6), s('Weber', 6), s('Mallet', 6), s('Hamon', 6),
  s('Boulanger', 6), s('Jacob', 6), s('Monnier', 6), s('Michaud', 6), s('Rodriguez', 6),
  s('Guichard', 5), s('Gillet', 5), s('Étienne', 5), s('Grondin', 5), s('Poulain', 5),
  s('Tessier', 5), s('Chevallier', 5), s('Collin', 5), s('Chauvin', 5), s('Bouchet', 5),
  s('Lemaître', 5), s('Bénard', 5), s('Maréchal', 5), s('Humbert', 5), s('Reynaud', 5),
  s('Antoine', 5), s('Hoarau', 5), s('Perret', 5), s('Barthélemy', 5), s('Cordier', 5),
  s('Pichon', 5), s('Lejeune', 5), s('Gilbert', 5), s('Lamy', 5), s('Delaunay', 5),
  s('Pasquier', 4), s('Carlier', 4), s('Laporte', 4),
  // Portuguese, Spanish, Italian heritage
  s('Da Silva', 9), s('Dos Santos', 8), s('Ferreira', 7), s('Pereira', 7), s('Rodrigues', 7),
  s('Gonçalves', 6), s('Fernandes', 6), s('Lopes', 6), s('Gomes', 6), s('Martins', 6),
  s('Rossi', 4), s('Russo', 3), s('Diaz', 4),
  // Maghreb heritage
  s('Benali', 6), s('Haddad', 6), s('Mansouri', 5), s('Brahimi', 5), s('Belkacem', 5),
  s('Saidi', 5), s('Bouzid', 5), s('Benamar', 4), s('Hamidi', 4), s('Cherif', 5),
  s('Benyahia', 3), s('Boudjema', 3), s('Meziane', 4), s('Amrani', 4), s('Bensaïd', 3),
  s('Khelifi', 4), s('Mebarki', 3), s('Hadjadj', 3), s('Ziani', 3), s('Touati', 4),
  s('Lahlou', 3), s('El Amrani', 4), s('Benmoussa', 3), s('Belhadj', 4), s('Djebbar', 3),
  s('Rahmani', 4), s('Boumediene', 3), s('Kaci', 4), s('Aït Ahmed', 3), s('Larbi', 3),
  s('Chaoui', 3), s('Bouaziz', 4), s('Messaoudi', 4), s('Slimani', 4), s('Benchikh', 3),
  s('Ouali', 3), s('Taleb', 3), s('Zerrouki', 3), s('Moussaoui', 4), s('Benabdallah', 3),
  s('Ferhat', 3), s('Bekkouche', 2), s('Hamdi', 4), s('Ammari', 3), s('Belaïd', 3),
  s('Mokrani', 3), s('Aït Ali', 3), s('Bensalem', 3), s('Bouchareb', 2), s('Kaddour', 3),
  s('El Idrissi', 3), s('Alaoui', 4), s('Bennani', 3), s('Tazi', 2), s('Berrada', 2),
  s('Chraibi', 2), s('Naciri', 2), s('El Mansouri', 3), s('Benhamou', 3), s('Boukhari', 2),
  s('Hadj', 2), s('Djelloul', 2), s('Ghazi', 2), s('Mekki', 2), s('Yahiaoui', 3),
  // West-African heritage
  s('Diallo', 6), s('Traoré', 6), s('Diop', 5), s('Ndiaye', 5), s('Camara', 5),
  s('Sylla', 4), s('Keita', 4), s('Cissé', 4), s('Coulibaly', 5), s('Konaté', 4),
  s('Touré', 4), s('Sow', 4), s('Bah', 3), s('Fofana', 3), s('Doumbia', 3),
  // Vietnamese heritage
  s('Nguyen', 6), s('Tran', 3), s('Le', 3), s('Pham', 2),
];
